import { CommonModule } from "@angular/common";
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Pipe,
  PipeTransform,
  ViewChild,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import {
  CATEGORY_API_URL,
  GLOBAL_SETTINGS_API_URL,
  LISTING_API_URL,
  USER_API_URL,
} from "@app/data/const";
import { Category, Listing } from "@app/data/listing";
import { RemoteDataService } from "@nowinstore/nowinstore-core-ui/components";
import * as _ from "lodash";
import { MessageService, TreeDragDropService, TreeNode } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { CheckboxModule } from "primeng/checkbox";
import { InputTextModule } from "primeng/inputtext";
import { MultiSelectModule } from "primeng/multiselect";
import { TreeModule } from "primeng/tree";
import { TreeTableModule } from "primeng/treetable";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { ToastModule } from "primeng/toast";
import { GlobalSettings } from "@app/services/global-settings.service";
import { User } from "@app/data/user";
import { DialogModule } from "primeng/dialog";
import { ListingsPickerComponent } from "./listings-picker.component";
import { ThumbnalizePipe } from "@app/pipe/thumbnalize.pipe";
import { SidebarModule } from "primeng/sidebar";

enum StatusType {
  Active = "ACTIVE",
  Draft = "DRAFT",
  Archived = "ARCHIVED",
  Publish = "publish",
}
interface Column {
  field: string;
  header: string;
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    MultiSelectModule,
    InputTextModule,
    CheckboxModule,
    TreeModule,
    FormsModule,
    ButtonModule,
    TreeTableModule,
    ProgressSpinnerModule,
    ToastModule,
    ThumbnalizePipe,
    SidebarModule,
    ListingsPickerComponent,
  ],
  selector: "nis-catalog-listings-editor",
  templateUrl: "./catalog-listings-editor.component.html",
  providers: [TreeDragDropService, MessageService],
  styles: [
    `
      :host
        ::ng-deep
        .p-tree
        .p-tree-container
        .p-treenode
        .p-treenode-content {
        display: flex;
        flex-direction: row;
        align-items: flex-start;
      }

      :host ::ng-deep .p-treetable .p-treetable-tbody > tr > td {
        padding: 0;
      }

      :host ::ng-deep .p-sidebar .p-sidebar-header {
        display: flex;
        flex-direction: row-reverse;
      }
    `,
  ],
})
export class CatalogListingsEditorComponent implements OnInit {
  @ViewChild(ListingsPickerComponent)
  listingsPickerComponent: ListingsPickerComponent;

  showListingsPicker: boolean = false;
  columns: Column[] = [
    { field: "name", header: $localize`Name` },
    { field: "action", header: $localize`Action` },
  ];
  isReordering: boolean = false;
  _nodesSelected: TreeNode<Listing | Category>[] = [];
  // _selectionKeys: Record<string, any> = {};
  urls: string[] = [CATEGORY_API_URL, LISTING_API_URL];

  loading: boolean;
  rows: number = 10;

  @Input()
  limit: number = 10;

  nodes: {
    value: TreeNode<Listing | Category>[];
    filteredValue: TreeNode<Listing | Category>[];
    selection: TreeNode<Listing | Category>[];
  } = {
    value: [],
    filteredValue: [],
    selection: [],
  };
  selectionNodes: TreeNode<Listing | Category>[] = [];
  nodesByKeys: { [key: string]: TreeNode<Listing | Category> } = {};
  filteredNodesByKeys: { [key: string]: TreeNode<Listing | Category> } = {};

  @Output()
  selectionChange = new EventEmitter<TreeNode<Listing | Category>[]>();

  _isOverLimit: boolean = false;

  selectedCount: number = 0;

  @Input()
  set isOverLimit(value: boolean) {
    if (this._isOverLimit !== value) {
      this._isOverLimit = value;
      this.isOverLimitChange.emit(this._isOverLimit);
    }
  }

  get isOverLimit(): boolean {
    return this._isOverLimit;
  }

  @Output()
  isOverLimitChange = new EventEmitter<boolean>();

  constructor(
    private messageService: MessageService,
    private remoteDataService: RemoteDataService
  ) {}

  async ngOnInit(): Promise<void> {
    const user = await this.remoteDataService.get<User>(USER_API_URL);
    this.limit = user.subscription.plan.limits.max_products_per_catalog;
  }

  @Input()
  set nodesSelected(nodes: TreeNode<Listing | Category>[]) {
    if (this._nodesSelected !== nodes) {
      this._nodesSelected = nodes;
      // this.updateKeysFromSelection();
      this.selectionChange.emit(this._nodesSelected);
    }
  }

  get nodesSelected(): TreeNode<Listing | Category>[] {
    return this._nodesSelected;
  }

  removeFromSelection() {
    const nodes = [];
    for (const node of this.nodes.value) {
      if (node && !this.nodes.selection.includes(node)) {
        const newNode = { ...node, children: [] };
        for (const childNode of node.children) {
          if (!this.nodes.selection.includes(childNode)) {
            newNode.children.push(childNode);
          }
        }
        nodes.push(newNode);
      }
    }
    this.nodes.value = nodes;
    this.nodes.selection = [];

    this.selectionChange.emit(this.nodes.value);
  }

  addNewCategory() {
    const uniqueKey = `${Date.now()}`;
    const parentNode = {
      key: uniqueKey,
      data: { title: "New Category" } as Category,
      kind: "category",
    };
    this.addToSelectionNodes(parentNode);
  }

  addToSelectionNodes(node: TreeNode<Listing | Category>) {
    this.nodes.value.push(node);
    this.nodes.value = [...this.nodes.value];
  }
  editCategory() {
    const selectedCategory = this.nodes.selection[0];
  }

  isANode(event) {
    return event.originalEvent.currentTarget.classList.value
      .toString()
      .includes("p-treenode-dragover");
  }
  onNodeDrop(event) {
    console.log(event);
    if (
      (event.dragNode.kind === "listing" &&
        event.dropNode.kind === "category" &&
        this.isANode(event)) ||
      (event.dragNode.kind === "listing" &&
        event.dropNode.kind === "listing" &&
        !this.isANode(event)) ||
      (event.dragNode.kind === "category" &&
        event.dropNode.kind === "category" &&
        !this.isANode(event))
    ) {
      event.accept();
    } else {
      this.messageService.add({
        severity: "info",
        summary: $localize`Category Selection`,
        detail: $localize`You cannot drop ${
          event.dragNode.kind === "category" ? "categories" : "listings"
        } into other ${
          event.dropNode.kind === "category" ? "categories" : "listings"
        }.`,
        key: "picker-toast",
      });
      event.reject();
    }
  }

  async addToSelection() {
    for (const node of this.selectionNodes) {
      // const unselectedNode = {
      //   ...node,
      //   checked: false,
      //   partialSelected: false,
      // };
      if (node) {
        Object.assign(node, {
          checked: false,
          partialSelected: false,
        });
        if (node.parent) {
          //current node is a child
          let selectionParentNode = this.nodes.value.find(
            (selectionNode) => selectionNode.key === node.parent.key
          );
          if (!selectionParentNode) {
            //parent node does not exist in selection
            selectionParentNode = Object.assign(node.parent, {
              checked: false,
              partialSelected: false,
              children: [node],
            });

            this.addToSelectionNodes(selectionParentNode);
          } else {
            if (
              !selectionParentNode.children.some(
                (childNode) => childNode.key === node.key
              )
            ) {
              selectionParentNode.children.push(node);
              this.nodes.value = [...this.nodes.value];
            }
          }
        } else {
          //parent node loading
          if (
            !this.nodes.value.some(
              (selectionNode) => selectionNode.key === node.key
            )
          ) {
            this.addToSelectionNodes(node);
          }
        }
      }
    }
    // }
    this.selectionNodes = [];

    this.selectionChange.emit(this.nodes.value);
    this.showListingsPicker = false;
  }

  @Output() isLoadingChildrenChange = new EventEmitter<boolean>();

  // Add a setter for isLoadingChildren
  @Input()
  set isLoadingChildren(value: boolean) {
    if (this._isLoadingChildren !== value) {
      this._isLoadingChildren = value;
      this.isLoadingChildrenChange.emit(this._isLoadingChildren);
    }
  }

  // Add a getter for isLoadingChildren
  get isLoadingChildren(): boolean {
    return this._isLoadingChildren;
  }

  onChildrenLoaded() {
    this.selectedCount = 0;
    for (const node of this.nodes.value) {
      this.selectedCount += node.children?.length || 0;
    }
    this.isOverLimit = this.selectedCount > this.limit;
  }

  // Add a private property to store the value
  private _isLoadingChildren: boolean;
}
